import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { ModalButtonWithForm } from 'components/common';
import {
  EmailMessageEditor,
  FormGroup,
  RadioGroup
} from 'components/common/form';

import type { EmailTemplateData, UserWithBasicData } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: (params: any) => Promise<void>;
  users: UserWithBasicData[];
}

const GroupUsersInvite = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers,
  users
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { group }
  } = useReduxState();

  const handleSubmit = async (values) => {
    const data = {
      emailContent: values.email,
      recipientsType: values.recipientsType
    };

    const response = await api.post(
      `/admin/groups/${group.id}/users-invite`,
      data
    );
    await updateUsers({});

    let successMessage = `${response.data.sentEmails} ${messages.commonSendEmailInvitesSuccess}`;

    let isWarning = false;
    const errorEmails = Object.keys(response.data.errors);

    if (errorEmails && errorEmails.length) {
      isWarning = true;
      successMessage += `<br /><br /> ${messages.commonSendEmailInvitesError}`;
      errorEmails.forEach((errorEmail) => {
        successMessage += `<br />${errorEmail}`;
      });
    }

    dispatch.addNotice({
      text: successMessage,
      type: isWarning ? 'warning' : 'success'
    });
  };

  if (!emailTemplatesDefault) return null;

  const initialValues = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    recipientsType: 'new-and-uninvited'
  };

  return (
    <ModalButtonWithForm
      buttonDisabled={!users || !users.length}
      buttonIcon="paper-plane"
      buttonSize="header"
      buttonText={messages.commonSendEmailInvites}
      buttonTextHideBreakpoint={breakpoints.medium - 1}
      initialValues={initialValues}
      modalActionText={messages.commonSend}
      modalTitle={messages.commonSendEmailInvites}
      modalWidth="wide"
      onSubmit={handleSubmit}
    >
      {() => (
        <div>
          <FormGroup label={messages.emailRecipientsSend}>
            <RadioGroup
              field="recipientsType"
              options={[
                {
                  id: 'email-recipients-all',
                  label: messages.emailRecipientsAll,
                  value: 'all'
                },
                {
                  id: 'email-recipients-uninvited',
                  label: messages.emailRecipientsUninvited,
                  value: 'uninvited'
                },
                {
                  id: 'email-recipients-new',
                  label: messages.emailRecipientsNewAndUninvited,
                  value: 'new-and-uninvited'
                }
              ]}
            />
          </FormGroup>

          <EmailMessageEditor
            availableTemplates={emailTemplates}
            id="group-users-invite"
            style={{ marginTop: 20 }}
          />
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default GroupUsersInvite;
